import { colors } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTheme } from 'styled-components';
import { $Keys, $Values, Optional } from 'utility-types';

import { SIMPLE_DATE_FORMAT } from 'apis/merchant-gateway/insights';
import { useThemeMode } from 'common/themeMode';

import CustomTooltip from './CustomTooltip';
import { ChartData, LegendChartType, Legends, FormatterFn, XAxisDataKey } from './types';
import { measureTextWidth } from './utils';

export type CurrencyConfig = Currency | Optional<Record<LegendChartType, Currency>>;
export type ValueFormatterConfig = FormatterFn | Optional<Record<LegendChartType, FormatterFn>>;
export type VerticalBarChartProps<LegendType extends Legends> = {
  data: ChartData<LegendType>;
  legends: LegendType;
  isReverseBars?: boolean;
  isStacked?: boolean;
  currency?: CurrencyConfig;
  axisDomains?: Optional<Record<LegendChartType, [number, number]>>;
  axisIntegerOnly?: Optional<Record<LegendChartType, boolean>>;
  summaryKey?: string;
  valueFormatter?: ValueFormatterConfig;
};

const Y_AXIS_TICK_MARGIN = 16;

const ticksCount = 5; // this is a recharts default

const calculateYAxisWidth = <LegendType extends Legends>(
  legends: LegendType,
  data: ChartData<LegendType>,
  axisChartType: LegendChartType,
  currencyFormatter: (amount: number) => string,
) => {
  const legendKeys = (Object.entries(legends) as [$Keys<LegendType>, $Values<LegendType>][])
    .filter(([, { type }]) => type === axisChartType)
    .map(([key]) => key);

  const longestYAxisTickWidth = Math.max(
    ...data.flatMap(barChartDataPoint => {
      const dataPoints = legendKeys.map(key => barChartDataPoint[key] ?? 0);
      const maxTickValue = dataPoints.reduce((acc, curr) => acc + curr, 0);
      const tickValueStep = maxTickValue / (ticksCount - 1);

      return new Array(ticksCount - 1)
        .fill(0)
        .map((_, index) => measureTextWidth(currencyFormatter(tickValueStep * (index + 1))));
    }),
  );
  return longestYAxisTickWidth + Y_AXIS_TICK_MARGIN;
};

const rightYAxisId = 'y-right';
const xAxisDataKey: XAxisDataKey = 'name';

const VerticalBarChart = <LegendType extends Legends>({
  data,
  isStacked,
  currency,
  axisDomains,
  axisIntegerOnly,
  legends,
  isReverseBars,
  summaryKey,
  valueFormatter,
}: VerticalBarChartProps<LegendType>) => {
  const { cbDirection } = useTheme();
  const { themeMode } = useThemeMode();
  const intl = useIntl();

  const barsCurrency = typeof currency === 'string' ? currency : currency?.[LegendChartType.BAR];
  const linesCurrency = typeof currency === 'string' ? undefined : currency?.[LegendChartType.LINE];

  const tickFormatterBars = useMemo(() => {
    if (typeof valueFormatter === 'function') {
      return valueFormatter;
    }

    const currencyFormatterBars = (amount: number) =>
      intl.formatNumber(amount, barsCurrency ? { currency: barsCurrency, style: 'currency' } : {});

    return valueFormatter?.[LegendChartType.BAR] || currencyFormatterBars;
  }, [valueFormatter, intl, barsCurrency]);

  const tickFormatterLines = useMemo(() => {
    if (typeof valueFormatter === 'function') {
      return valueFormatter;
    }

    const currencyFormatterLines = (amount: number) =>
      intl.formatNumber(amount, linesCurrency ? { currency: linesCurrency, style: 'currency' } : {});

    return valueFormatter?.[LegendChartType.LINE] || currencyFormatterLines;
  }, [valueFormatter, intl, linesCurrency]);

  const formatXAxisTick = (value: string) => {
    const localizedDateLabel =
      value !== null
        ? DateTime.fromFormat(value, SIMPLE_DATE_FORMAT)
            .setLocale(intl.locale)
            .toLocaleString({ month: '2-digit', day: '2-digit' })
        : '';
    return localizedDateLabel;
  };

  // We need to dynamically measure the width of the longest tick for the Y-axis because
  // recharts.js doesn't do it for us, which causes tickes to be hidden when it's getting longer.
  const measuredBarsYAxisWidth = calculateYAxisWidth(legends, data, LegendChartType.BAR, tickFormatterBars);
  const measuredLinesYAxisWidth = calculateYAxisWidth(legends, data, LegendChartType.LINE, tickFormatterLines);

  const lineKeys = Object.entries(legends)
    .filter(([, { type }]) => type === LegendChartType.LINE)
    .map(([key]) => key);

  return (
    <ResponsiveContainer width="100%" height={250}>
      <ComposedChart data={data}>
        <CartesianGrid vertical={false} stroke={themeMode === 'light' ? colors.pepper4 : colors.salt12} />
        <XAxis
          dataKey={xAxisDataKey}
          tick={{ fill: themeMode === 'light' ? colors.pepper100 : colors.salt100, fontSize: '12px' }}
          stroke="none"
          reversed={cbDirection !== 'ltr'}
          tickFormatter={formatXAxisTick}
        />
        <YAxis
          tickFormatter={tickFormatterBars}
          tick={{
            fill: themeMode === 'light' ? colors.pepper64 : colors.salt72,
            fontSize: '12px',
            direction: 'ltr', // we need to set this to ltr because of a bug in recharts.js
          }}
          width={measuredBarsYAxisWidth}
          orientation={cbDirection === 'ltr' ? 'left' : 'right'}
          stroke="none"
          domain={
            axisDomains?.[LegendChartType.BAR] ||
            (axisIntegerOnly?.[LegendChartType.BAR] && [
              0,
              (maxValue: number | unknown) => (typeof maxValue === 'number' && Math.ceil(maxValue)) || 1,
            ]) ||
            undefined
          }
          allowDecimals={!axisIntegerOnly?.[LegendChartType.BAR]}
        />
        <YAxis
          yAxisId={rightYAxisId}
          orientation={cbDirection === 'ltr' ? 'right' : 'left'}
          tickFormatter={tickFormatterLines}
          tick={{
            fill: themeMode === 'light' ? colors.pepper64 : colors.salt72,
            fontSize: '12px',
            direction: 'ltr', // we need to set this to ltr because of a bug in recharts.js
          }}
          axisLine={false}
          stroke="none"
          width={measuredLinesYAxisWidth}
          domain={
            axisDomains?.[LegendChartType.LINE] ||
            (axisIntegerOnly?.[LegendChartType.LINE] && [
              0,
              (maxValue: number | unknown) => (typeof maxValue === 'number' && Math.ceil(maxValue)) || 1,
            ]) ||
            undefined
          }
          allowDecimals={!axisIntegerOnly?.[LegendChartType.LINE]}
        />
        <Tooltip
          content={
            <CustomTooltip
              isReverse={isReverseBars}
              summaryKey={summaryKey}
              ignoreKeysForSummaryCalc={lineKeys}
              summaryCurrency={barsCurrency}
              formatter={tickFormatterBars}
            />
          }
          cursor={{ fill: themeMode === 'light' ? colors.pepper8 : colors.salt4 }}
        />
        {(isReverseBars ? Object.entries(legends).reverse() : Object.entries(legends)).map(
          ([key, { value, color, type }]) => {
            return type === LegendChartType.BAR ? (
              <Bar
                key={key}
                dataKey={key}
                name={value}
                stackId={isStacked ? 0 : undefined}
                fill={color}
                radius={[2, 2, 0, 0]}
                layout="horizontal"
                unit={barsCurrency}
              />
            ) : (
              <Line
                key={key}
                strokeWidth={2}
                dataKey={key}
                stroke={color}
                name={value}
                dot={false}
                yAxisId={rightYAxisId}
                unit={linesCurrency}
              />
            );
          },
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
export default VerticalBarChart;
